type DefaultEnumValueType = string | number;

export interface EnumValue<KeyType extends string = string, ValueType = DefaultEnumValueType> {
    name: KeyType;
    value: ValueType;
    toString(): string;
}

export type Enumify<KeyType extends string = string, ValueType = DefaultEnumValueType> = {
    [key in KeyType]: EnumValue<KeyType, ValueType>;
} & {
    enumValues: Array<EnumValue<KeyType, ValueType>>;
    getByName(name: KeyType): EnumValue<KeyType, ValueType>;
    getByValue(value: ValueType): EnumValue<KeyType, ValueType> | undefined;
};

export type EnumConfig<KeyType extends string = string, ValueType = DefaultEnumValueType> = {
    [key in KeyType]: ValueType;
};

export function enumify<KeyType extends string = string, ValueType = DefaultEnumValueType>(
    enumeration: () => void,
    enumName: string,
    values: EnumConfig<KeyType, ValueType>
) {
    if (typeof enumeration !== 'function') {
        throw new Error('Expected the enumeration to be a function.');
    }

    if (enumName.length === 0) {
        throw new Error("Expected the enumeration's name to be a non-empty string.");
    }

    if (!isPlainObject(values)) {
        throw TypeError('Values of enumeration expected as object');
    }

    const enumValues = initEnumValues(values);
    const enumProperties = keyBy(enumValues, 'name');

    Object.assign(enumeration, enumProperties, {
        enumValues,

        getByName(name: KeyType) {
            return enumValues.find((v) => v.name === name);
        },

        getByValue(value: ValueType) {
            return enumValues.find((x) => x.value === value);
        },

        toString() {
            return enumName;
        },
    });

    Object.freeze(enumeration);

    function initEnumValues(args: EnumConfig<KeyType, ValueType>) {
        const hasValueProperty = (
            candidate: ValueType
        ): candidate is Extract<ValueType, Record<'value', unknown> & Record<string, unknown>> =>
            typeof candidate === 'object' &&
            candidate !== null &&
            Object.prototype.hasOwnProperty.call(candidate, 'value');

        return (Object.keys(args) as Array<KeyType>).map((name) => {
            const value = args[name];
            let mapped: EnumValue<KeyType, ValueType> & Record<string, unknown>;

            if (hasValueProperty(value)) {
                mapped = {
                    name,
                    ...value,
                } as EnumValue<KeyType, ValueType> & Record<string, unknown>;
            } else {
                mapped = {
                    name,
                    value,
                };
            }

            mapped.toString = function toString() {
                return enumName + name;
            };

            return create(enumeration, mapped);
        });
    }
}

function create<T extends object>(type: () => void, ...mixins: T[]): T {
    const instance = Object.create(type.prototype) as T;
    return Object.assign(instance, ...mixins);
}

function isObjectLike(value: {}) {
    return typeof value === 'object' && value !== null;
}

function getStringTag(value: {}) {
    return Object.prototype.toString.call(value);
}

function isPlainObject(value: {}) {
    if (!isObjectLike(value) || getStringTag(value) !== '[object Object]') {
        return false;
    }

    const proto = Object.getPrototypeOf(value);
    return proto === Object.prototype || proto === null;
}

function keyBy<T extends object>(collection: T, property: string) {
    return Object.keys(collection).reduce((result, valueKey) => {
        // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'string' can't be used to index type 'unknown'.
        const value = collection[valueKey];

        const key = value[property];
        // @ts-expect-error $TSFixMeError: Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
        result[key] = value;
        return result;
    }, {});
}
